import * as THREE from 'three';
import { materialLibrary as M } from '../MaterialLibrary';
import { SIGNAL } from '../MaterialLibrary';

export type BoatModel = {
  root: THREE.Group;
  hull: THREE.Mesh;
  engines: THREE.Object3D[];
  throttleFlames: THREE.Mesh[];
  collisionRadius: number;
};

function taperedHullGeometry(): THREE.BufferGeometry {
  // Authored hull: V-profile cross sections lofted along the length.
  const sections = 14;
  const length = 4.4;
  const positions: number[] = [];
  const indices: number[] = [];

  for (let i = 0; i < sections; i++) {
    const t = i / (sections - 1);
    const z = (t - 0.5) * length;
    // hull half width profile: wide midship, pointed bow, squared stern
    const half = Math.sin(Math.PI * Math.min(1, 0.12 + t * 0.92)) * 0.92;
    const deckY = 0.42 + Math.sin(t * Math.PI) * 0.1;
    const keelY = -0.34 - Math.sin(t * Math.PI * 0.9) * 0.16;
    // 5 cross-section points: port rail, port chine, keel, starboard chine, starboard rail
    const pts: Array<[number, number]> = [
      [-half, deckY],
      [-half * 0.8, (deckY + keelY) * 0.42],
      [0, keelY],
      [half * 0.8, (deckY + keelY) * 0.42],
      [half, deckY],
    ];
    for (const [x, y] of pts) positions.push(x, y, z);
  }

  for (let i = 0; i < sections - 1; i++) {
    const a = i * 5;
    const b = (i + 1) * 5;
    for (let j = 0; j < 4; j++) {
      indices.push(a + j, a + j + 1, b + j + 1);
      indices.push(a + j, b + j + 1, b + j);
    }
  }
  // stern cap
  const base = (sections - 1) * 5;
  indices.push(base, base + 1, base + 2);
  indices.push(base, base + 2, base + 3);
  indices.push(base, base + 3, base + 4);

  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  geometry.setIndex(indices);
  geometry.computeVertexNormals();
  return geometry;
}

export function createBoat(): BoatModel {
  const root = new THREE.Group();

  // Hull
  const hull = new THREE.Mesh(taperedHullGeometry(), M.bodyPrimary);
  hull.castShadow = true;
  hull.receiveShadow = true;
  root.add(hull);

  // Red racing stripe decal: thin extruded strip over hull sides
  const stripeShape = new THREE.Shape();
  stripeShape.moveTo(-0.02, -2.1);
  stripeShape.lineTo(0.02, -2.1);
  stripeShape.lineTo(0.06, 1.55);
  stripeShape.lineTo(-0.06, 1.55);
  stripeShape.closePath();
  const stripe = new THREE.Mesh(
    new THREE.ExtrudeGeometry(stripeShape, { depth: 0.03, bevelEnabled: false }),
    M.bodySecondary,
  );
  stripe.position.set(0.84, 0.36, 0);
  stripe.rotation.y = Math.PI / 2;
  root.add(stripe);
  const stripe2 = stripe.clone();
  stripe2.position.x = -0.84;
  root.add(stripe2);

  // Deck + cockpit
  const deck = new THREE.Mesh(new THREE.BoxGeometry(1.35, 0.18, 2.6), M.whitePaint);
  deck.position.set(0, 0.5, 0.15);
  deck.castShadow = true;
  root.add(deck);

  const cockpitTub = new THREE.Mesh(new THREE.BoxGeometry(0.98, 0.34, 1.2), M.bodySecondary);
  cockpitTub.position.set(0, 0.66, 0.42);
  root.add(cockpitTub);

  // Windscreen: faceted wedge glass
  const screenGeo = new THREE.BufferGeometry();
  screenGeo.setAttribute(
    'position',
    new THREE.Float32BufferAttribute(
      [
        -0.42, 0.62, -0.28, 0.42, 0.62, -0.28, 0.34, 1.05, -0.72, -0.34, 1.05, -0.72,
        0.42, 0.62, -0.28, 0.34, 1.05, -0.72, 0.3, 0.62, 0.55, 0.26, 1.05, 0.62,
      ],
      3,
    ),
  );
  screenGeo.setIndex([0, 1, 2, 0, 2, 3, 1, 4, 5, 1, 5, 6]);
  screenGeo.computeVertexNormals();
  const windscreen = new THREE.Mesh(screenGeo, M.glass);
  windscreen.position.set(0, 0.22, 0.28);
  root.add(windscreen);

  // Racing seats
  const seat = new THREE.Mesh(new THREE.BoxGeometry(0.3, 0.42, 0.12), new THREE.MeshStandardMaterial({ color: '#14181d', roughness: 0.7 }));
  seat.position.set(-0.24, 0.82, 0.86);
  seat.rotation.x = -0.12;
  root.add(seat);
  const seat2 = seat.clone();
  seat2.position.x = 0.24;
  root.add(seat2);

  // Steering console
  const consoleBlock = new THREE.Mesh(new THREE.BoxGeometry(0.9, 0.22, 0.3), M.trim);
  consoleBlock.position.set(0, 0.8, 0.18);
  root.add(consoleBlock);
  const wheel = new THREE.Mesh(new THREE.TorusGeometry(0.13, 0.02, 8, 24), M.trim);
  wheel.position.set(0, 0.92, 0.16);
  wheel.rotation.x = 0.35;
  root.add(wheel);

  // Spoiler
  const spoilerPostGeo = new THREE.BoxGeometry(0.06, 0.3, 0.08);
  for (const x of [-0.4, 0.4]) {
    const post = new THREE.Mesh(spoilerPostGeo, M.trim);
    post.position.set(x, 0.72, 1.72);
    root.add(post);
  }
  const wing = new THREE.Mesh(new THREE.BoxGeometry(1.5, 0.05, 0.34), M.bodySecondary);
  wing.position.set(0, 0.9, 1.72);
  wing.rotation.x = -0.12;
  wing.castShadow = true;
  root.add(wing);
  const wingTrim = new THREE.Mesh(new THREE.BoxGeometry(1.5, 0.02, 0.06), M.trim);
  wingTrim.position.set(0, 0.93, 1.86);
  root.add(wingTrim);

  // Engines: two outboards with propellers
  const engines: THREE.Object3D[] = [];
  const throttleFlames: THREE.Mesh[] = [];
  const engineGroup = new THREE.Group();
  for (const side of [-1, 1]) {
    const eng = new THREE.Group();
    const cowl = new THREE.Mesh(new THREE.CylinderGeometry(0.12, 0.16, 0.42, 10), M.bodyPrimary);
    cowl.position.y = 0.62;
    eng.add(cowl);
    const cowlCap = new THREE.Mesh(new THREE.SphereGeometry(0.12, 10, 8), M.bodySecondary);
    cowlCap.position.y = 0.84;
    eng.add(cowlCap);
    const shaft = new THREE.Mesh(new THREE.CylinderGeometry(0.035, 0.035, 0.5, 8), M.chrome);
    shaft.position.y = 0.2;
    eng.add(shaft);
    const prop = new THREE.Mesh(new THREE.ConeGeometry(0.11, 0.16, 6), M.chrome);
    prop.position.y = -0.02;
    prop.rotation.x = Math.PI;
    eng.add(prop);
    const skeg = new THREE.Mesh(new THREE.BoxGeometry(0.04, 0.3, 0.1), M.chrome);
    skeg.position.y = 0.1;
    skeg.position.z = -0.09;
    eng.add(skeg);
    eng.position.set(side * 0.42, -0.1, 2.18);
    engineGroup.add(eng);
    engines.push(eng);

    // Throttle flame socket (hidden; shown during boost)
    const flame = new THREE.Mesh(
      new THREE.ConeGeometry(0.09, 0.5, 8),
      new THREE.MeshBasicMaterial({ color: SIGNAL.boost, transparent: true, opacity: 0.9 }),
    );
    flame.position.set(side * 0.42, 0.62, 2.32);
    flame.rotation.x = Math.PI / 2;
    flame.visible = false;
    engineGroup.add(flame);
    throttleFlames.push(flame);
  }
  root.add(engineGroup);

  // Nose trim + nav light
  const noseTrim = new THREE.Mesh(new THREE.TorusGeometry(0.1, 0.02, 8, 20), M.chrome);
  noseTrim.position.set(0, 0.55, -2.0);
  noseTrim.rotation.x = Math.PI / 2;
  root.add(noseTrim);
  const navLight = new THREE.Mesh(new THREE.SphereGeometry(0.04, 8, 8), M.emissiveSignal);
  navLight.position.set(0, 0.62, -2.05);
  root.add(navLight);

  // Rub rail: dark trim strip around hull silhouette
  const rail = new THREE.Mesh(new THREE.TorusGeometry(1.02, 0.035, 8, 40), M.trim);
  rail.rotation.x = Math.PI / 2;
  rail.scale.set(1, 1, 2.15);
  rail.position.y = 0.4;
  root.add(rail);

  root.rotation.y = 0;
  return { root, hull, engines, throttleFlames, collisionRadius: 2.3 };
}
